import { useState, useMemo } from 'react';
import { useCharacterBenefits } from '../../../hooks/useCharacterBenefits';
import { useCharacterCreationStore } from '../../../stores/characterCreationStore';
import { formatAbility } from '../../../utils/formatting';
import {
  getSpellsForClassAtLevel,
  getCantripsForClass,
  getSpell,
  getClass,
} from '@character-creator/data';
import type { Spell, RacialSpellcastingInfo } from '@character-creator/types';
import { FamiliarSelector } from '../FamiliarSelector';

function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function formatSpellLevel(level: number): string {
  if (level === 0) return 'Cantrip';
  if (level === 1) return '1st';
  if (level === 2) return '2nd';
  if (level === 3) return '3rd';
  return `${level}th`;
}

function getComponentsString(spell: Spell): string {
  const parts: string[] = [];
  if (spell.components.verbal) parts.push('V');
  if (spell.components.somatic) parts.push('S');
  if (spell.components.material) parts.push('M');
  return parts.join(', ');
}

export function SpellsStep() {
  const benefits = useCharacterBenefits();
  const {
    draft,
    selectCantrip,
    deselectCantrip,
    selectKnownSpell,
    deselectKnownSpell,
    setFamiliar,
  } = useCharacterCreationStore();

  const [cantripSearch, setCantripSearch] = useState('');
  const [spellSearch, setSpellSearch] = useState('');
  const [expandedSpellId, setExpandedSpellId] = useState<string | null>(null);

  const hasSpellcasting = benefits?.spellcasting !== null;
  const hasRacialSpells = (benefits?.racialSpellcasting?.length ?? 0) > 0;

  // Get primary spellcasting class data
  const primarySpellcastingClass = useMemo(() => {
    for (const cl of draft.classLevels) {
      const classData = getClass(cl.classId);
      if (classData?.spellcasting) {
        return { classData, level: cl.level };
      }
    }
    return null;
  }, [draft.classLevels]);

  // Get spell slots for current level
  const spellSlots = useMemo(() => {
    if (!primarySpellcastingClass?.classData.spellcasting) return [];
    const slots = primarySpellcastingClass.classData.spellcasting.spellSlots;
    const levelIndex = Math.min(primarySpellcastingClass.level, 20) - 1;
    return slots[levelIndex] || [];
  }, [primarySpellcastingClass]);

  // Get spells known limit (for known casters like Bard, Sorcerer)
  const spellsKnownLimit = useMemo(() => {
    if (!primarySpellcastingClass?.classData.spellcasting?.spellsKnown) return null;
    const spellsKnown = primarySpellcastingClass.classData.spellcasting.spellsKnown;
    const levelIndex = Math.min(primarySpellcastingClass.level, 20) - 1;
    return spellsKnown[levelIndex] || 0;
  }, [primarySpellcastingClass]);

  // Available cantrips for the class
  const availableCantrips = useMemo(() => {
    if (!primarySpellcastingClass) return [];
    const classCantrips = getCantripsForClass(primarySpellcastingClass.classData.id);
    if (!cantripSearch) return classCantrips;
    const query = cantripSearch.toLowerCase();
    return classCantrips.filter((spell) => spell.name.toLowerCase().includes(query));
  }, [primarySpellcastingClass, cantripSearch]);

  // All spells for the class (stable unless class/slots change)
  const allClassSpells = useMemo(() => {
    if (!primarySpellcastingClass) return [];

    let maxSpellLevel = 0;
    for (let i = spellSlots.length - 1; i >= 0; i--) {
      if (spellSlots[i] > 0) {
        maxSpellLevel = i + 1;
        break;
      }
    }

    const spells: Spell[] = [];
    for (let level = 1; level <= maxSpellLevel; level++) {
      spells.push(
        ...getSpellsForClassAtLevel(primarySpellcastingClass.classData.id, level)
      );
    }
    return spells;
  }, [primarySpellcastingClass, spellSlots]);

  // Filtered spells (only re-runs on search change, not full data rebuild)
  const availableSpells = useMemo(() => {
    if (!spellSearch) return allClassSpells;
    const query = spellSearch.toLowerCase();
    return allClassSpells.filter((spell) => spell.name.toLowerCase().includes(query));
  }, [allClassSpells, spellSearch]);

  // Group available spells by level
  const spellsByLevel = useMemo(() => {
    const grouped: Record<number, Spell[]> = {};
    for (const spell of availableSpells) {
      if (!grouped[spell.level]) {
        grouped[spell.level] = [];
      }
      grouped[spell.level].push(spell);
    }
    return grouped;
  }, [availableSpells]);

  const cantripsKnown = benefits?.spellcasting?.cantripsKnown || 0;
  const selectedCantripCount = draft.selectedCantrips.length;
  const selectedSpellCount = draft.knownSpells.length;

  const toggleCantrip = (spellId: string) => {
    if (draft.selectedCantrips.includes(spellId)) {
      deselectCantrip(spellId);
    } else if (selectedCantripCount < cantripsKnown) {
      selectCantrip(spellId);
    }
  };

  const toggleSpell = (spellId: string) => {
    if (draft.knownSpells.includes(spellId)) {
      deselectKnownSpell(spellId);
      // Clear familiar if Find Familiar is deselected
      if (spellId === 'find-familiar') {
        setFamiliar(null);
      }
    } else if (spellsKnownLimit === null || selectedSpellCount < spellsKnownLimit) {
      selectKnownSpell(spellId);
    }
  };

  const toggleExpandSpell = (spellId: string) => {
    setExpandedSpellId((prev) => (prev === spellId ? null : spellId));
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h2 className="font-display text-2xl font-bold text-text-primary mb-2">
          Spellcasting
        </h2>
        <p className="text-text-secondary">
          {hasSpellcasting
            ? 'Manage your spells and magical abilities.'
            : hasRacialSpells
              ? 'Your race grants innate spellcasting abilities.'
              : 'Your class does not have spellcasting abilities.'}
        </p>
      </div>

      {/* Racial Spells Section — shown for any character with racial spell grants */}
      {hasRacialSpells && benefits?.racialSpellcasting && (
        <div className="mb-6">
          {benefits.racialSpellcasting.map((info) => (
            <RacialSpellsCard key={info.sourceTrait} info={info} />
          ))}
        </div>
      )}

      {hasSpellcasting && benefits?.spellcasting ? (
        <div className="space-y-6">
          {/* Spellcasting Stats */}
          <div className="card p-6">
            <h3 className="font-semibold text-accent-gold text-lg mb-4">Spellcasting Ability</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4 rounded-lg bg-background-secondary">
                <div className="text-3xl font-bold text-accent-gold">
                  {formatAbility(benefits.spellcasting.ability).slice(0, 3).toUpperCase()}
                </div>
                <div className="text-xs text-text-muted mt-2">Ability</div>
              </div>
              <div className="p-4 rounded-lg bg-background-secondary">
                <div className="text-3xl font-bold text-text-primary">
                  {benefits.spellcasting.saveDC}
                </div>
                <div className="text-xs text-text-muted mt-2">Spell Save DC</div>
              </div>
              <div className="p-4 rounded-lg bg-background-secondary">
                <div className="text-3xl font-bold text-text-primary">
                  {benefits.spellcasting.attackBonus >= 0 ? '+' : ''}
                  {benefits.spellcasting.attackBonus}
                </div>
                <div className="text-xs text-text-muted mt-2">Spell Attack</div>
              </div>
            </div>
          </div>

          {/* Spell Slots */}
          {spellSlots.some((count) => count > 0) && (
            <div className="card p-6">
              <h3 className="font-semibold text-text-primary mb-4">Spell Slots</h3>
              <div className="flex flex-wrap gap-4">
                {spellSlots.map(
                  (count, index) =>
                    count > 0 && (
                      <div key={index} className="text-center">
                        <div className="text-sm text-text-muted mb-1">
                          {formatSpellLevel(index + 1)}
                        </div>
                        <div className="flex gap-1">
                          {Array.from({ length: count }).map((_, i) => (
                            <div
                              key={i}
                              className="w-4 h-4 rounded-full bg-accent-gold"
                            />
                          ))}
                        </div>
                      </div>
                    )
                )}
              </div>
            </div>
          )}

          {/* Cantrips Selection */}
          {cantripsKnown > 0 && (
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-text-primary">Cantrips</h3>
                <span className="text-sm text-text-muted">
                  {selectedCantripCount}/{cantripsKnown} selected
                </span>
              </div>

              {/* Search */}
              <div className="relative mb-4">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  type="text"
                  placeholder="Search cantrips..."
                  value={cantripSearch}
                  onChange={(e) => setCantripSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-background-secondary border border-border-default rounded-lg text-text-primary placeholder:text-text-muted text-sm focus:outline-none focus:border-accent-gold"
                />
              </div>

              {/* Selected Cantrips */}
              {draft.selectedCantrips.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs text-text-muted mb-2">Selected</div>
                  <div className="flex flex-wrap gap-2">
                    {draft.selectedCantrips.map((spellId) => {
                      const spell = getSpell(spellId);
                      if (!spell) return null;
                      return (
                        <button
                          key={spellId}
                          onClick={() => toggleCantrip(spellId)}
                          className="px-3 py-1.5 bg-accent-gold text-background-primary rounded-lg text-sm font-medium flex items-center gap-2"
                        >
                          {spell.name}
                          <span className="text-xs">×</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Available Cantrips */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {availableCantrips
                  .filter((spell) => !draft.selectedCantrips.includes(spell.id))
                  .map((spell) => (
                    <SpellSelectCard
                      key={spell.id}
                      spell={spell}
                      isSelected={false}
                      isDisabled={selectedCantripCount >= cantripsKnown}
                      isExpanded={expandedSpellId === spell.id}
                      onToggle={() => toggleCantrip(spell.id)}
                      onExpand={() => toggleExpandSpell(spell.id)}
                    />
                  ))}
              </div>

              {availableCantrips.length === 0 && (
                <p className="text-center text-text-muted py-4">
                  No cantrips found matching your search.
                </p>
              )}
            </div>
          )}

          {/* Spells Selection */}
          {(spellsKnownLimit === null || spellsKnownLimit > 0) &&
            spellSlots.some((count) => count > 0) && (
              <div className="card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-text-primary">
                    {spellsKnownLimit !== null ? 'Known Spells' : 'Available Spells'}
                  </h3>
                  {spellsKnownLimit !== null && (
                    <span className="text-sm text-text-muted">
                      {selectedSpellCount}/{spellsKnownLimit} known
                    </span>
                  )}
                </div>

                {/* Search */}
                <div className="relative mb-4">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                  <input
                    type="text"
                    placeholder="Search spells..."
                    value={spellSearch}
                    onChange={(e) => setSpellSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 bg-background-secondary border border-border-default rounded-lg text-text-primary placeholder:text-text-muted text-sm focus:outline-none focus:border-accent-gold"
                  />
                </div>

                {/* Selected Spells */}
                {draft.knownSpells.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs text-text-muted mb-2">Selected</div>
                    <div className="flex flex-wrap gap-2">
                      {draft.knownSpells.map((spellId) => {
                        const spell = getSpell(spellId);
                        if (!spell) return null;
                        return (
                          <button
                            key={spellId}
                            onClick={() => toggleSpell(spellId)}
                            className="px-3 py-1.5 bg-accent-gold text-background-primary rounded-lg text-sm font-medium flex items-center gap-2"
                          >
                            {spell.name}{' '}
                            <span className="opacity-70">({formatSpellLevel(spell.level)})</span>
                            <span className="text-xs">×</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Familiar Selector - shown when Find Familiar is selected */}
                {draft.knownSpells.includes('find-familiar') && (
                  <div className="mb-4">
                    <FamiliarSelector
                      selectedFamiliarId={draft.selectedFamiliarId}
                      onSelect={setFamiliar}
                      compact={!!draft.selectedFamiliarId}
                    />
                  </div>
                )}

                {/* Available Spells by Level */}
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {Object.entries(spellsByLevel)
                    .sort(([a], [b]) => Number(a) - Number(b))
                    .map(([level, spells]) => (
                      <div key={level}>
                        <div className="text-sm font-semibold text-accent-gold mb-2">
                          {formatSpellLevel(Number(level))} Level
                        </div>
                        <div className="space-y-2">
                          {spells
                            .filter((spell) => !draft.knownSpells.includes(spell.id))
                            .map((spell) => (
                              <SpellSelectCard
                                key={spell.id}
                                spell={spell}
                                isSelected={false}
                                isDisabled={
                                  spellsKnownLimit !== null &&
                                  selectedSpellCount >= spellsKnownLimit
                                }
                                isExpanded={expandedSpellId === spell.id}
                                onToggle={() => toggleSpell(spell.id)}
                                onExpand={() => toggleExpandSpell(spell.id)}
                              />
                            ))}
                        </div>
                      </div>
                    ))}
                </div>

                {availableSpells.length === 0 && (
                  <p className="text-center text-text-muted py-4">
                    No spells found matching your search.
                  </p>
                )}
              </div>
            )}
        </div>
      ) : !hasRacialSpells ? (
        <div className="card p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-secondary flex items-center justify-center">
            <WandIcon className="w-8 h-8 text-text-muted" />
          </div>
          <h3 className="font-semibold text-text-primary text-lg mb-2">No Spellcasting</h3>
          <p className="text-text-muted max-w-md mx-auto">
            Your current class doesn't have spellcasting abilities. If you'd like to cast
            spells, consider multiclassing into a spellcasting class or choosing a different
            primary class.
          </p>
        </div>
      ) : null}
    </div>
  );
}

function RacialSpellsCard({ info }: { info: RacialSpellcastingInfo }) {
  return (
    <div className="card p-6 mb-4">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="font-semibold text-accent-gold text-lg">{info.sourceTrait}</h3>
        <span className="px-2 py-0.5 text-xs font-medium bg-accent-purple/20 text-accent-purple rounded">
          Innate
        </span>
      </div>

      <div className="grid grid-cols-3 gap-4 text-center mb-4">
        <div className="p-3 rounded-lg bg-background-secondary">
          <div className="text-xl font-bold text-accent-gold">
            {formatAbility(info.ability).slice(0, 3).toUpperCase()}
          </div>
          <div className="text-xs text-text-muted mt-1">Ability</div>
        </div>
        <div className="p-3 rounded-lg bg-background-secondary">
          <div className="text-xl font-bold text-text-primary">{info.saveDC}</div>
          <div className="text-xs text-text-muted mt-1">Save DC</div>
        </div>
        <div className="p-3 rounded-lg bg-background-secondary">
          <div className="text-xl font-bold text-text-primary">
            {info.attackBonus >= 0 ? '+' : ''}{info.attackBonus}
          </div>
          <div className="text-xs text-text-muted mt-1">Spell Attack</div>
        </div>
      </div>

      {info.cantrips.length > 0 && (
        <div className="mb-3">
          <div className="text-xs text-text-muted uppercase mb-2">Cantrips (At Will)</div>
          <div className="flex flex-wrap gap-2">
            {info.cantrips.map((spellId) => {
              const spell = getSpell(spellId);
              return (
                <span
                  key={spellId}
                  className="px-3 py-1.5 bg-background-secondary border border-border-default rounded-lg text-sm text-text-primary"
                >
                  {spell?.name ?? spellId}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {info.spells.length > 0 && (
        <div>
          <div className="text-xs text-text-muted uppercase mb-2">Spells</div>
          <div className="space-y-2">
            {info.spells.map((spell) => (
              <div
                key={spell.spellId}
                className="flex items-center justify-between px-3 py-2 bg-background-secondary border border-border-default rounded-lg"
              >
                <div>
                  <span className="text-sm font-medium text-text-primary">{spell.spellName}</span>
                  <span className="text-xs text-text-muted ml-2">
                    ({formatSpellLevel(spell.spellLevel)} level)
                  </span>
                </div>
                {spell.usesPerRest && (
                  <span className="text-xs text-text-muted">
                    {spell.usesPerRest}/{spell.restType} rest
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface SpellSelectCardProps {
  spell: Spell;
  isSelected: boolean;
  isDisabled: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onExpand: () => void;
}

function SpellSelectCard({
  spell,
  isSelected,
  isDisabled,
  isExpanded,
  onToggle,
  onExpand,
}: SpellSelectCardProps) {
  return (
    <div
      className={`border rounded-lg overflow-hidden transition-colors ${
        isSelected
          ? 'border-accent-gold bg-accent-gold/10'
          : 'border-border-default bg-background-secondary'
      }`}
    >
      <div className="flex items-start gap-3 p-3">
        <button
          onClick={onToggle}
          disabled={isDisabled && !isSelected}
          className={`mt-0.5 w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
            isSelected
              ? 'bg-accent-gold border-accent-gold text-background-primary'
              : isDisabled
                ? 'border-border-default cursor-not-allowed opacity-50'
                : 'border-border-default hover:border-accent-gold'
          }`}
        >
          {isSelected && <CheckIcon className="w-3 h-3" />}
        </button>

        <div className="flex-1 min-w-0">
          <button
            onClick={onExpand}
            className="w-full text-left"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-text-primary">{spell.name}</span>
              {spell.ritual && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-accent-purple/20 text-accent-purple rounded">
                  R
                </span>
              )}
              {spell.concentration && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-accent-blue/20 text-accent-blue rounded">
                  C
                </span>
              )}
            </div>
            <div className="text-xs text-text-muted mt-0.5">
              {getComponentsString(spell)} &bull; {spell.castingTime} &bull;{' '}
              {capitalizeFirst(spell.school)}
            </div>
          </button>

          {isExpanded && (
            <div className="mt-3 pt-3 border-t border-border-default">
              <p className="text-sm text-text-secondary leading-relaxed">{spell.description}</p>
              {spell.higherLevel && (
                <div className="mt-2">
                  <span className="text-xs font-semibold text-text-primary">At Higher Levels: </span>
                  <span className="text-xs text-text-secondary">{spell.higherLevel}</span>
                </div>
              )}
              <div className="mt-2 text-xs text-text-muted">
                Range: {spell.range} &bull; Duration: {spell.duration}
              </div>
            </div>
          )}
        </div>

        <button
          onClick={onExpand}
          className="p-1 text-text-muted hover:text-text-primary transition-colors"
        >
          <ChevronIcon
            className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </button>
      </div>
    </div>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function WandIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
      />
    </svg>
  );
}
